import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${API_BASE}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auth
export const signup = (data) => api.post('/auth/signup', data);
export const login = (data) => api.post('/auth/login', data);
export const googleSession = (sessionId) => api.post('/auth/google-session', { session_id: sessionId });
export const getMe = () => api.get('/auth/me');
export const logout = () => api.post('/auth/logout');

// Collectibles
export const getCollectibles = () => api.get('/collectibles');
export const getCollectible = (id) => api.get(`/collectibles/${id}`);
export const getCollectiblePrice = (id) => api.get(`/collectibles/${id}/price`);
export const lockCollectiblePrice = (id) => api.post(`/collectibles/${id}/lock-price`);

// Certificate Verification
export const verifyCertificateBySerial = (serial) => api.get(`/certificate/${serial}`);

// Drops
export const getDrops = () => api.get('/drops');
export const getFeaturedDrop = () => api.get('/drops/featured');
export const getDrop = (id) => api.get(`/drops/${id}`);

// Metal Rates
export const getCurrentRates = () => api.get('/metal-rates/current');
export const getMetalHistory = (metalType, period) => api.get(`/metal-rates/history/${metalType}?period=${period}`);

// Calculator
export const calculatePrice = (data) => api.post('/calculator', data);

// Purchase
export const purchaseCollectible = (collectibleId, lockToken, delivery) => api.post('/purchase', { collectible_id: collectibleId, lock_token: lockToken, delivery });

// Checkout & Payment
export const initiateCheckout = (collectibleId, lockToken, delivery) => api.post('/checkout/initiate', { collectible_id: collectibleId, lock_token: lockToken, delivery });
export const getPaymentConfig = () => api.get('/payment/config');
export const createPaymentOrder = (orderId) => api.post('/payment/create-order', { order_id: orderId });
export const verifyPayment = (data) => api.post('/payment/verify', data);
export const cancelPaymentOrder = (orderId, reason) => api.post('/payment/cancel-order', { order_id: orderId, reason });
export const getPaymentStatus = (orderId) => api.get(`/payment/status/${orderId}`);

// Portfolio
export const getPortfolio = () => api.get('/portfolio');

// Education
export const getEducation = () => api.get('/education');
export const getEducationItem = (id) => api.get(`/education/${id}`);

// Notifications
export const getNotifications = () => api.get('/notifications');

// Cart (Chest)
export const getCart = () => api.get('/cart');
export const addToCart = (collectibleId) => api.post('/cart/add', { collectible_id: collectibleId });
export const removeFromCart = (collectibleId) => api.delete(`/cart/${collectibleId}`);

// News
export const getNews = () => api.get('/news');

// Estimated Rates
export const getEstimatedRates = () => api.get('/metal-rates/estimated');

// Purity Standards
export const getPurityStandards = () => api.get('/purity-standards');

// Orders
export const getOrders = () => api.get('/orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const getOrderTracking = (id) => api.get(`/orders/${id}/tracking`);

// Certificates
export const getCertificate = (id) => api.get(`/certificates/${id}`);
export const verifyCertificate = (id) => api.get(`/certificates/verify/${id}`);
export const getUserCertificates = () => api.get('/user/certificates');

// Admin - Certificates
export const getAdminCertificates = () => api.get('/admin/certificates');
export const updateEngraving = (certId) => api.put(`/admin/certificates/${certId}/engraving`);

// FAQ
export const getFaqs = () => api.get('/faqs');

// Admin
export const getAdminStats = () => api.get('/admin/stats');
export const getAdminOrders = () => api.get('/admin/orders');
export const updateOrderStatus = (orderId, status) => api.put('/admin/orders/status', { order_id: orderId, status });
export const updateOrderShipping = (orderId, data) => api.put(`/admin/orders/${orderId}/shipping`, data);
export const adminDeleteOrder = (orderId) => api.delete(`/admin/orders/${orderId}`);
export const getAdminSalesHub = () => api.get('/admin/sales-hub');
export const adminCreateSale = (data) => api.post('/admin/sales', data);
export const adminUpdateSale = (saleId, data) => api.put(`/admin/sales/${saleId}`, data);
export const adminDeleteSale = (saleId) => api.delete(`/admin/sales/${saleId}`);
export const getAdminUsers = () => api.get('/admin/users');

// Admin - Drops
export const getAdminDrops = () => api.get('/admin/drops');
export const adminCreateDrop = (data) => api.post('/admin/drops', data);
export const adminUpdateDrop = (dropId, data) => api.put(`/admin/drops/${dropId}`, data);
export const adminDeleteDrop = (dropId) => api.delete(`/admin/drops/${dropId}`);

// Admin - Collectibles
export const getAdminCollectibles = () => api.get('/admin/collectibles');
export const adminCreateCollectible = (data) => api.post('/admin/collectibles', data);
export const adminUpdateCollectible = (collectibleId, data) => api.put(`/admin/collectibles/${collectibleId}`, data);
export const adminDeleteCollectible = (collectibleId) => api.delete(`/admin/collectibles/${collectibleId}`);

// Admin - News
export const getAdminNews = () => api.get('/admin/news');
export const adminCreateNews = (data) => api.post('/admin/news', data);
export const adminDeleteNews = (newsId) => api.delete(`/admin/news/${newsId}`);

// Image Upload
export const uploadImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Wishlist
export const getWishlist = () => api.get('/wishlist');
export const addToWishlist = (collectibleId) => api.post('/wishlist/add', { collectible_id: collectibleId });
export const removeFromWishlist = (collectibleId) => api.delete(`/wishlist/${collectibleId}`);
export const checkWishlist = (collectibleId) => api.get(`/wishlist/check/${collectibleId}`);

// Custom Request
export const createCustomRequest = (data) => api.post('/custom-request', data);
export const getAdminCustomRequests = () => api.get('/admin/custom-requests');
export const getAdminPendingCustomRequests = () => api.get('/admin/custom-requests/pending');
export const updateCustomRequestStatus = (requestId, status, adminNotes, estimatedPrice, estimatedDays) => 
  api.put(`/admin/custom-requests/${requestId}/status?status=${status}${adminNotes ? '&admin_notes=' + encodeURIComponent(adminNotes) : ''}${estimatedPrice ? '&estimated_price=' + estimatedPrice : ''}${estimatedDays ? '&estimated_delivery_days=' + estimatedDays : ''}`);

// Categories
export const getCategories = () => api.get('/categories');
export const getAdminCategories = () => api.get('/admin/categories');
export const adminCreateCategory = (data) => api.post('/admin/categories', data);
export const adminDeleteCategory = (name) => api.delete(`/admin/categories/${name}`);

// Platform Settings
export const getPublicSettings = () => api.get('/settings/public');
export const getAdminSettings = () => api.get('/admin/settings');
export const updateAdminSettings = (data) => api.put('/admin/settings', data);
export const getAdminIbjaRate = () => api.get('/admin/ibja-rate');
export const updateAdminIbjaRate = (data) => api.put('/admin/ibja-rate', data);

// User Management
export const getAdminUsersList = () => api.get('/admin/users');
export const getAdminUser = (userId) => api.get(`/admin/users/${userId}`);
export const adminCreateUser = (data) => api.post('/admin/users', data);
export const adminUpdateUser = (userId, data) => api.put(`/admin/users/${userId}`, data);
export const adminDeleteUser = (userId) => api.delete(`/admin/users/${userId}`);
export const adminResetUserPassword = (userId) => api.post(`/admin/users/${userId}/reset-password`);

// Contact
export const submitContact = (data) => api.post('/contact', data);
export const getAdminMessages = () => api.get('/admin/messages');

// Search
export const searchCollectibles = (query, filters = {}) => {
  const params = new URLSearchParams({ q: query });
  if (filters.metal_type) params.append('metal_type', filters.metal_type);
  if (filters.design_type) params.append('design_type', filters.design_type);
  return api.get(`/search?${params.toString()}`);
};

// Profit
export const getPortfolioProfit = () => api.get('/portfolio/profit');

// ─── Pothdar AI Engine ────────────────────────────────────────
export const pothdarGenerateConcepts = (data) => api.post('/pothdar/generate-concepts', data);
export const pothdarGetDesigns = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/pothdar/designs${q ? '?' + q : ''}`);
};
export const pothdarCompareDesigns = (ids) => api.post('/pothdar/compare-designs', { design_ids: ids });
export const pothdarScoreDesign = (data) => api.post('/pothdar/score-design', data);
export const pothdarApproveDesign = (id) => api.post('/pothdar/approve-design', { design_id: id });
export const pothdarPredictSales = (id) => api.post('/pothdar/predict-sales', { design_id: id });
export const pothdarPrepManufacturing = (id) => api.post('/pothdar/prepare-manufacturing', { design_id: id });
export const pothdarGenerate3D = (id, detail) => api.post('/pothdar/generate-3d-model', { design_id: id, detail_level: detail || 'standard' });
export const pothdarGenMarketing = (id, style) => api.post('/pothdar/generate-marketing-assets', { design_id: id, marketing_style: style || 'luxury-minimal' });
export const pothdarGetComponents = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/pothdar/components${q ? '?' + q : ''}`);
};
export const pothdarCreateComponent = (data) => api.post('/pothdar/components', data);
export const pothdarGetMemory = (params = {}) => {
  const q = new URLSearchParams(params).toString();
  return api.get(`/pothdar/design-memory${q ? '?' + q : ''}`);
};
export const pothdarGetBrandDNA = () => api.get('/pothdar/brand-dna');
export const pothdarGetJobs = () => api.get('/pothdar/jobs');
export const pothdarGetJob = (id) => api.get(`/pothdar/jobs/${id}`);

// Pothdar Interactive AI
export const pothdarChat = (message, sessionId, context) => api.post('/pothdar/chat', { message, session_id: sessionId, context });
export const pothdarGenerateImage = (prompt, style, designId) => api.post('/pothdar/generate-image', { prompt, style, design_id: designId });
export const pothdarGenerateVideo = (prompt, duration, size, designId) => api.post('/pothdar/generate-video', { prompt, duration, size, design_id: designId });
export const pothdarGetMedia = (type) => api.get(`/pothdar/media${type ? '?media_type=' + type : ''}`);
export const pothdarDeleteMedia = (mediaId) => api.delete(`/pothdar/media/${mediaId}`);
export const pothdarGetMediaItem = (mediaId) => api.get(`/pothdar/media/${mediaId}`);

// Seed
export const seedData = () => api.post('/seed');

export default api;

export const getAdminNotificationHealth = () => api.get('/admin/notifications/health');
export const sendAdminNotificationTest = (channel) => api.post('/admin/notifications/test', { channel });
